import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ScopeProvider } from "@/context/ScopeContext";
import { Loading } from "@/components/common/Common";
import AppShell from "@/components/layout/AppShell";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import { LoginPage, RegisterPage } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import GoogleAds from "@/pages/GoogleAds";
import Campaigns from "@/pages/Campaigns";
import CampaignWizard from "@/pages/CampaignWizard";
import CampaignEditor from "@/pages/campaign/CampaignEditor";
import Optimizer from "@/pages/Optimizer";
import Diagnostics from "@/pages/Diagnostics";
import Assistant from "@/pages/Assistant";
import Reports from "@/pages/Reports";
import Clients from "@/pages/Clients";
import Subscription from "@/pages/Subscription";
import SettingsPage from "@/pages/Settings";
import Admin from "@/pages/admin/Admin";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ChannelsHub from "@/pages/channels/ChannelsHub";
import MetaChannel from "@/pages/channels/MetaChannel";
import YouTubeChannel from "@/pages/channels/YouTubeChannel";
import WhatsAppChannel from "@/pages/channels/WhatsAppChannel";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordReset";

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  if (!admin && !user.onboarding_done && user.role !== "admin" && loc.pathname !== "/app/onboarding") return <Navigate to="/app/onboarding" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="cz_theme">
      <TooltipProvider delayDuration={150}>
        <AuthProvider>
          <BrowserRouter>
            <ScopeProvider>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/planos" element={<Pricing />} />
                <Route path="/privacidade" element={<Privacy />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/termos" element={<Terms />} />
                <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
                <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/cadastro" element={<RegisterPage />} />
                <Route path="/app/onboarding" element={<Protected><Onboarding /></Protected>} />
                <Route path="/app" element={<Protected><AppShell /></Protected>}>
                  <Route index element={<Dashboard />} />
                  <Route path="google-ads" element={<GoogleAds />} />
                  <Route path="campaigns" element={<Campaigns />} />
                  <Route path="campaigns/new" element={<CampaignWizard />} />
                  <Route path="campaigns/:id" element={<CampaignEditor />} />
                  <Route path="optimizer" element={<Optimizer />} />
                  <Route path="diagnostico" element={<Diagnostics />} />
                  <Route path="assistente" element={<Assistant />} />
                  <Route path="relatorios" element={<Reports />} />
                  <Route path="clientes" element={<Clients />} />
                  <Route path="assinatura" element={<Subscription />} />
                  <Route path="conta" element={<SettingsPage />} />
                  <Route path="canais" element={<ChannelsHub />} />
                  <Route path="canais/facebook" element={<MetaChannel key="facebook" platform="facebook" />} />
                  <Route path="canais/instagram" element={<MetaChannel key="instagram" platform="instagram" />} />
                  <Route path="canais/youtube" element={<YouTubeChannel />} />
                  <Route path="canais/whatsapp" element={<WhatsAppChannel />} />
                </Route>
                <Route path="/admin" element={<Protected admin><AppShell /></Protected>}>
                  <Route index element={<Admin />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ScopeProvider>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
